import { Box, Portal } from '@mui/material'
import { memo, useContext, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import * as styled from './RefField.styled'

import { FieldWrapper, Icon, Name, RefPicker, RemoveContent } from 'components'
import { CMSContext, ProgressContext } from 'context'
import { useOnClickOutside } from 'hooks'
import { CollectionRecord } from 'pages/CMS/pages'
import { LogicItem } from 'pages/Settings/pages/Logic/pages'
import { CMSCollectionRecord } from 'services/cms'
import { LogicService } from 'services/logic'
import { ConfigLogic, generateFirestoreId } from 'utils'
import { FieldProps } from '../../types'
import { ReferenceItem, ReferenceItems } from './ReferenceItems'

interface RefFieldProps extends FieldProps {
  value: CMSCollectionRecord | CMSCollectionRecord[] | string | string[] | null
  onChange: (val: CMSCollectionRecord | CMSCollectionRecord[] | string | string[] | null) => void
}

export const RefField = memo((props: RefFieldProps) => {
  const { rootCollections, assets } = useContext(CMSContext)
  const { startLoader, stopLoader, toast } = useContext(ProgressContext)
  const { id } = useParams()
  const [toRemove, setToRemove] = useState<CMSCollectionRecord | null>(null)
  const [openMenu, setOpenMenu] = useState(false)
  const [openLogicItem, setOpenLogicItem] = useState(false)
  const [openExisting, setOpenExisting] = useState(false)
  const [recordId, setRecordId] = useState('')
  const currentCollection = props.logicItem
    ? null
    : props.resourceType
    ? assets
    : rootCollections.find(el => el.id === props.collection)
  const logicItemName = props.name?.slice(0, -1)
  const pickerTitle =
    (props.logicItem ? logicItemName : props.resourceType ? props.resourceType : 'record') +
    `${props.multiple ? 's' : ''}`
  const value = !props.multiple ? (props.value as CMSCollectionRecord | string | null) : null
  const arrayValue = props.multiple ? (props.value as CMSCollectionRecord[] | string[] | null) || [] : []
  const ref = useRef<HTMLDivElement>(null)
  useOnClickOutside(ref, () => setOpenMenu(false))

  const onAddNewRef = (val: CMSCollectionRecord | string) => {
    if (props.multiple) {
      props.onChange([
        // @ts-ignore
        ...arrayValue.filter(el =>
          props.resourceType ? (el as CMSCollectionRecord).id !== (val as CMSCollectionRecord).id : el !== val
        ),
        val,
      ])
    } else {
      props.onChange(val)
    }
  }

  const onAddExistingRef = (val: CMSCollectionRecord | string) => {
    if (props.multiple) {
      // @ts-ignore
      const active = arrayValue.find(el =>
        props.resourceType ? (el as CMSCollectionRecord).id === (val as CMSCollectionRecord).id : el === val
      )
      if (active) {
        // @ts-ignore
        const value = arrayValue.filter(el =>
          props.resourceType ? (el as CMSCollectionRecord).id !== (active as CMSCollectionRecord).id : el !== active
        )
        props.onChange(value.length ? value : null)
      } else {
        props.onChange([...arrayValue, val])
      }
    } else {
      props.onChange(val)
      setOpenExisting(false)
    }
  }

  const remove = (val: CMSCollectionRecord | string) => {
    if (props.multiple) {
      // @ts-ignore
      const value = arrayValue.filter(el =>
        props.resourceType ? (el as CMSCollectionRecord).id !== (val as CMSCollectionRecord).id : el !== val
      )
      props.onChange(value.length ? value : null)
    } else {
      props.onChange(null)
    }
  }

  const removeLogicItem = (callback: () => void) => {
    if (toRemove) {
      startLoader()
      LogicService.deleteLogicItem(id as string, props.name as string, toRemove.name)
        .catch(err => toast(err))
        .finally(() => {
          callback()
          stopLoader()
        })
    }
  }

  return (
    <>
      <FieldWrapper
        {...props}
        children={props => (
          <>
            {!value ? (
              <styled.RefFieldInput
                onClick={e => {
                  e.preventDefault()
                  if (props.logicItem) {
                    setOpenLogicItem(open => !open)
                  } else {
                    setOpenMenu(open => !open)
                  }
                }}
                ref={ref}
              >
                <span>
                  <Icon name={Name.RIGHT_SIDEBAR_ADD} />
                  Add {pickerTitle}
                  {openMenu && (
                    <Box>
                      <Box onClick={() => setOpenExisting(true)}>Add existing {pickerTitle}</Box>
                      <Box onClick={() => setRecordId(generateFirestoreId())}>Add new {pickerTitle}</Box>
                    </Box>
                  )}
                </span>
              </styled.RefFieldInput>
            ) : (
              <ReferenceItem
                value={value}
                remove={() => (props.logicItem ? setToRemove(value as CMSCollectionRecord) : remove(value))}
                open={setRecordId}
                name={props.name}
                logicItem={props.logicItem}
                resourceType={props.resourceType}
              />
            )}
            {props.multiple && (
              <ReferenceItems
                values={arrayValue}
                onChange={props.onChange}
                remove={val => (props.logicItem ? setToRemove(val as CMSCollectionRecord) : remove(val))}
                open={setRecordId}
                name={props.name}
                logicItem={props.logicItem}
                resourceType={props.resourceType}
              />
            )}
          </>
        )}
      />
      {openExisting && currentCollection && (
        <RefPicker
          currentRef={currentCollection}
          selectedValues={props.multiple ? arrayValue : []}
          multiple={props.multiple}
          close={() => setOpenExisting(false)}
          onAddRef={onAddExistingRef}
          resourceType={props.resourceType}
          title={pickerTitle}
        />
      )}
      {recordId && currentCollection && (
        <Portal>
          <CollectionRecord
            recordId={recordId}
            collectionId={currentCollection.id}
            currentCollection={currentCollection}
            resourceType={props.resourceType}
            onAddRef={onAddNewRef}
            closeAddRef={() => setRecordId('')}
          />
        </Portal>
      )}
      {props.logicItem && (openLogicItem || recordId) && (
        <Portal>
          <LogicItem
            name={props.name as keyof ConfigLogic}
            recordId={recordId}
            closeAddRef={() => {
              setOpenLogicItem(false)
              setRecordId('')
            }}
          />
        </Portal>
      )}
      {props.logicItem && (
        <RemoveContent
          toRemove={toRemove}
          title={`Delete ${logicItemName}`}
          text={`You are about to delete the ${logicItemName} <b>{name}</b>. To make sure, enter the ${logicItemName} name below`}
          clean={() => setToRemove(null)}
          remove={removeLogicItem}
          nameConfirmation
        />
      )}
    </>
  )
})
